// Shared mastery-tier visuals, replacing the old tiny Unicode emoji medals
// (🥉🥈🥇 rendered at ~11–16px, low-contrast, near-identical across the three
// metallic tiers). Two exports:
//   <TierIcon>    — the bare lucide glyph, colored by whatever container it sits
//                   in (used inline in legends, the heatmap, the Leverage Board).
//   <MasteryMedal>— a prominent color-coded pill (tier-gradient fill + icon),
//                   for the Practice-by-Topic cards where the medal is the
//                   headline signal that a topic is mastered.
import type { MasteryTier } from '@/lib/test-engine/sat-domains'
import { MASTERY_DISPLAY } from '@/lib/test-engine/sat-domains'
import { TIER_CELL, TIER_ICON } from '@/lib/scoring/mastery-display'

/** The bare tier glyph. Inherits color from its parent and size from className
 *  (defaults to a small inline size). Always decorative — pair it with a text
 *  label or wrap it in <MasteryMedal> for the accessible name. */
export function TierIcon({
  tier,
  className = 'h-3 w-3',
}: {
  tier: MasteryTier
  className?: string
}) {
  const Icon = TIER_ICON[tier]
  return <Icon className={className} aria-hidden="true" />
}

/** A prominent, color-coded mastery medal — a tier-gradient pill with the
 *  tier's lucide icon. Renders ONLY for earned medal tiers (bronze/silver/gold);
 *  returns null otherwise, since the un-earned tiers aren't medals and the
 *  card's own accent already signals them. */
export function MasteryMedal({ tier, className = '' }: { tier: MasteryTier; className?: string }) {
  if (tier !== 'bronze' && tier !== 'silver' && tier !== 'gold') return null
  const label = MASTERY_DISPLAY[tier].label
  return (
    <span
      role="img"
      aria-label={`${label} tier`}
      title={`${label} tier`}
      className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${TIER_CELL[tier]} ${className}`}
    >
      <TierIcon tier={tier} className="h-3.5 w-3.5" />
    </span>
  )
}
